import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { callAPI, HttpMethod } from '@/redux/saga/index'
import type {
  ResultHistoryResponse,
  ResultHistoryDateGroup,
  ResultHistoryRound,
  GameTranslation,
} from '@/types/resultHistory'
import type {
  YeekeeRoundRow,
  DateGroup,
  DailyStock,
  DailyGovernment,
  DailyGroup,
  DailyGroupSix,
} from '@/ui-components/components/ResultHistory'
import { DailyDataTypes } from '@/ui-components/components/ResultHistory/ResultHistory.types'

const isYeekeeOrMalta = (t?: string) => t === 'yeekee' || t === 'encrypt_game'
const isStock = (t?: string) => t === 'stock'
const isGovernment = (t?: string) => t === 'default'
const isGroupFour = (t?: string) => t === 'group'
const isGroupSix = (t?: string) => t === 'group_six'

// ─── API response → submodule type transformers ───────────────────────────────

function toRoundBased(
  items: ResultHistoryDateGroup[],
): DateGroup<YeekeeRoundRow>[] {
  return items.map((group) => ({
    date: group.date,
    rounds: group.rounds.map((r) => ({
      roundNumber: r.round_number,
      threeTop: r.top_three_result,
      twoUnder: r.bottom_two_result,
    })),
  }))
}

function toDailyStock(items: ResultHistoryDateGroup[]): DailyStock[] {
  return items.map((group) => {
    const r: Partial<ResultHistoryRound> = group.rounds[0] ?? {}
    return {
      kind: 'stock',
      date: group.date,
      threeTop: r.top_three_result ?? '',
      twoUnder: r.bottom_two_result ?? '',
    }
  })
}

function toDailyGovernment(items: ResultHistoryDateGroup[]): DailyGovernment[] {
  return items.map((group) => {
    const r: Partial<ResultHistoryRound> = group.rounds[0] ?? {}
    return {
      kind: 'government',
      date: group.date,
      threeTop: r.top_three_result ?? '',
      twoUnder: r.bottom_two_result ?? '',
      threeFront1: r.bottom_three_front_1_result ?? '',
      threeFront2: r.bottom_three_front_2_result ?? '',
      threeUnder1: r.bottom_three_1_result ?? '',
      threeUnder2: r.bottom_three_2_result ?? '',
      twoTop: r.result_top_two ?? '',
    }
  })
}

function toDailyGroup(items: ResultHistoryDateGroup[]): DailyGroup[] {
  return items.map((group) => ({
    kind: 'group',
    date: group.date,
    fourDigit: group.rounds[0]?.four_result ?? '',
  }))
}

function toDailyGroupSix(items: ResultHistoryDateGroup[]): DailyGroupSix[] {
  return items.map((group) => ({
    kind: 'group_six',
    date: group.date,
    sixDigit: group.rounds[0]?.six_result ?? '',
  }))
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

const useResultHistory = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { gameId: gameIdParam } = useParams<{ gameId: string }>()
  const gameId = Number(gameIdParam)

  const locationState = location.state as {
    gameName?: string
    gameType?: string
  } | null

  const [gameType, setGameType] = useState<string>(
    locationState?.gameType ?? '',
  )
  const [collapsedMap, setCollapsedMap] = useState<Record<string, boolean>>({})
  const [translations, setTranslations] = useState<GameTranslation[]>([])
  const [rawItems, setRawItems] = useState<ResultHistoryDateGroup[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const gameName = useMemo(() => {
    if (translations.length === 0) return locationState?.gameName ?? ''
    const match = translations.find((tr) => tr.language_code === i18n.language)
    return (match ?? translations[0]).name
  }, [translations, i18n.language, locationState?.gameName])

  const roundBasedData = useMemo<DateGroup<YeekeeRoundRow>[]>(
    () => (isYeekeeOrMalta(gameType) ? toRoundBased(rawItems) : []),
    [rawItems, gameType],
  )

  const dailyData = useMemo<DailyDataTypes[]>(() => {
    if (isStock(gameType)) return toDailyStock(rawItems)
    if (isGovernment(gameType)) return toDailyGovernment(rawItems)
    if (isGroupFour(gameType)) return toDailyGroup(rawItems)
    if (isGroupSix(gameType)) return toDailyGroupSix(rawItems)
    return []
  }, [rawItems, gameType])

  const fetchHistory = useCallback(async () => {
    if (!gameId) return

    setLoading(true)
    setRawItems([])

    const url = `${import.meta.env.VITE_HOST}/api/v1/lotto/results/${gameId}`

    try {
      const response = await callAPI({
        url: url.toString(),
        method: HttpMethod.GET,
      })
      const body: ResultHistoryResponse = response.data

      if (body.status !== 'OK') return

      const { game, items } = body.data

      if (game.type) setGameType(game.type)
      if (game.translations.length > 0) setTranslations(game.translations)
      setRawItems(items ?? [])
    } catch (error) {
      console.error('Failed to fetch result history:', error)
    } finally {
      setLoading(false)
    }
  }, [gameId])

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  const hasRoundBasedLayout = isYeekeeOrMalta(gameType)
  const hasDailyLayout =
    isStock(gameType) ||
    isGovernment(gameType) ||
    isGroupFour(gameType) ||
    isGroupSix(gameType)
  const hasAnyLayout = hasRoundBasedLayout || hasDailyLayout

  const hasFilteredData = hasRoundBasedLayout
    ? roundBasedData.length > 0
    : hasDailyLayout
      ? dailyData.length > 0
      : false

  const toggleCollapsed = (key: string) => {
    setCollapsedMap((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }))
  }

  return {
    t,
    loading,
    gameName,
    gameType,
    onBack: () => navigate(-1),
    collapsedMap,
    toggleCollapsed,
    hasRoundBasedLayout,
    hasAnyLayout,
    hasFilteredData,
    dailyData,
    roundBasedData,
  }
}

export default useResultHistory
